/**
 * My Competitors Tab - Gestiona tus competidores
 */
import { useEffect, useState, useCallback, forwardRef, useImperativeHandle } from 'react'
import { useAuth } from '@trafiflow/shared-ui'
import { Button } from '@trafiflow/shared-ui'
import { Plus, Edit, Trash2, Users } from 'lucide-react'
import apiClient from '../../../../shared/lib/api'
import type { Domain, Brand } from '../../types'
import DomainModal from '../../components/DomainModal'

export interface MyCompetitorsTabRef {
  refresh: () => void
}

const MyCompetitorsTab = forwardRef<MyCompetitorsTabRef>((_props, ref) => {
  const { user } = useAuth()
  const [domains, setDomains] = useState<Domain[]>([])
  const [brands, setBrands] = useState<Brand[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingDomain, setEditingDomain] = useState<Domain | null>(null)

  const fetchDomains = useCallback(async () => {
    if (!user?.tenantId) return

    try {
      setLoading(true)
      setError(null)
      const response = await apiClient.get('/my-company/domains?type=competitor')
      setDomains(response.data.domains || [])
    } catch (error: unknown) {
      const err = error as { response?: { status?: number; data?: { message?: string } }; message?: string }
      // If 404, treat as empty list (endpoint might not exist yet or no competitors)
      if (err.response?.status === 404) {
        setDomains([])
        setError(null)
      } else {
        console.error('Error fetching competitors:', err)
        setError(err.response?.data?.message || 'Failed to load competitors')
        setDomains([])
      }
    } finally {
      setLoading(false)
    }
  }, [user?.tenantId])

  const fetchBrands = useCallback(async () => {
    if (!user?.tenantId) return

    try {
      const response = await apiClient.get('/brands')
      setBrands(response.data.brands || [])
    } catch (err: unknown) {
      console.error('Error fetching brands:', err)
      // Silently fail - brands are optional
    }
  }, [user?.tenantId])

  useEffect(() => {
    fetchDomains()
    fetchBrands()
  }, [fetchDomains, fetchBrands])

  // Expose refresh function via ref
  useImperativeHandle(ref, () => ({
    refresh: () => {
      fetchDomains()
    },
  }))

  const handleCreate = () => {
    setEditingDomain(null)
    setIsModalOpen(true)
  }

  const handleEdit = (domain: Domain) => {
    setEditingDomain(domain)
    setIsModalOpen(true)
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this competitor?')) {
      return
    }

    try {
      await apiClient.delete(`/my-company/domains/${id}`)
      await fetchDomains()
    } catch (error: unknown) {
      const err = error as { response?: { status?: number; data?: { message?: string } }; message?: string }
      console.error('Error deleting competitor:', err)
      alert(err.response?.data?.message || 'Failed to delete competitor')
    }
  }

  const handleModalClose = () => {
    setIsModalOpen(false)
    setEditingDomain(null)
  }

  const handleModalSave = async () => {
    await fetchDomains()
    handleModalClose()
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <div className="w-10 h-10 border-2 border-tf-border border-t-primary rounded-full animate-spin mb-4" />
        <p className="text-sm font-medium text-tf-text-sub">Loading competitors...</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-tf-text">Competitors</h2>
          <p className="text-sm text-tf-text-muted mt-0.5">
            {domains.length} competitor {domains.length === 1 ? 'domain' : 'domains'} tracked
          </p>
        </div>
        <Button
          variant="primary"
          onClick={handleCreate}
          icon={<Plus className="w-4 h-4" />}
        >
          Add Competitor
        </Button>
      </div>

      {error && (
        <div className="rounded-lg bg-tf-feedback-error-bg border border-red-200 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {domains.length === 0 ? (
        <div className="text-center py-16 rounded-xl border-2 border-dashed border-tf-border bg-tf-bg-muted">
          <div className="w-14 h-14 rounded-xl bg-tf-bg-hover flex items-center justify-center mx-auto mb-4">
            <Users className="w-7 h-7 text-tf-text-muted" />
          </div>
          <h3 className="text-base font-semibold text-tf-text mb-1">No competitors yet</h3>
          <p className="text-sm text-tf-text-muted mb-6 max-w-sm mx-auto">
            Add competitor domains to track and analyze their presence
          </p>
          <Button
            variant="primary"
            onClick={handleCreate}
            icon={<Plus className="w-4 h-4" />}
          >
            Add Competitor
          </Button>
        </div>
      ) : (
        <div className="overflow-x-auto -mx-2 rounded-lg border border-tf-border">
          <table className="min-w-full">
            <thead>
              <tr className="bg-tf-bg-muted border-b border-tf-border">
                <th className="px-5 py-3.5 text-left text-xs font-semibold text-tf-text-muted uppercase tracking-wider">
                  Domain
                </th>
                <th className="px-5 py-3.5 text-left text-xs font-semibold text-tf-text-muted uppercase tracking-wider hidden xl:table-cell">
                  Facebook Page ID
                </th>
                <th className="px-5 py-3.5 text-left text-xs font-semibold text-tf-text-muted uppercase tracking-wider hidden md:table-cell">
                  Added
                </th>
                <th className="px-5 py-3.5 text-right text-xs font-semibold text-tf-text-muted uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-tf-border-sub">
              {domains.map((domain) => (
                <tr key={domain.id} className="bg-tf-bg-card hover:bg-tf-bg-muted transition-colors">
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-tf-bg-hover flex items-center justify-center">
                        <Users className="w-4 h-4 text-tf-text-muted" />
                      </div>
                      <span className="font-medium text-tf-text">{domain.domain}</span>
                    </div>
                  </td>
                  <td className="px-5 py-4 hidden xl:table-cell">
                    <code className="text-xs font-mono text-tf-text-muted bg-tf-bg-muted px-2 py-1 rounded">
                      {domain.facebookPageId || '—'}
                    </code>
                  </td>
                  <td className="px-5 py-4 hidden md:table-cell text-sm text-tf-text-muted">
                    {new Date(domain.createdAt).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </td>
                  <td className="px-5 py-4 text-right">
                    <div className="flex justify-end gap-1">
                      <button
                        onClick={() => handleEdit(domain)}
                        className="p-2 text-tf-text-muted hover:text-primary hover:bg-primary/5 rounded-lg transition-colors"
                        title="Edit"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(domain.id)}
                        className="p-2 text-tf-text-muted hover:text-red-600 hover:bg-tf-feedback-error-bg rounded-lg transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <DomainModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onSave={handleModalSave}
        domain={editingDomain}
        type="competitor"
        brands={brands}
      />
    </div>
  )
})

MyCompetitorsTab.displayName = 'MyCompetitorsTab'

export default MyCompetitorsTab

